import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  DollarSign, 
  TrendingUp, 
  Package, 
  CheckCircle,
  XCircle,
  Ban,
  AlertTriangle,
  Loader2,
  Store,
  ShoppingBag,
  Wallet,
  ArrowDownCircle,
  Download,
  Image as ImageIcon
} from 'lucide-react';
import * as XLSX from 'xlsx';
import StatCard from './StatCard';
import StatusBreakdownChart from './StatusBreakdownChart';
import TopSellersTable from './TopSellersTable';
import TopProductsTable from './TopProductsTable';
import LowStockAlerts from './LowStockAlerts';
import ImageCarouselModal from './ImageCarouselModal';

function Phase1Dashboard({ userId, period, customStartDate, customEndDate }) {
  const [financialData, setFinancialData] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [topSellers, setTopSellers] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [lowStock, setLowStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  const baseUrl = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    fetchAllData();
  }, [userId, period, customStartDate, customEndDate]);

  async function fetchAllData() {
    setLoading(true);
    setError(null);

    try {
      const params = {
        admin_user_id: userId,
        period: period
      };

      if (period === 'custom' && customStartDate && customEndDate) {
        params.start_date = customStartDate;
        params.end_date = customEndDate;
      }

      const [financial, performance, sellers, products, stock] = await Promise.all([
        axios.get(`${baseUrl}/api/stats/financial-overview`, { params }),
        axios.get(`${baseUrl}/api/stats/delivery-performance`, { params }),
        axios.get(`${baseUrl}/api/stats/top-sellers`, { params: { ...params, limit: 5 } }),
        axios.get(`${baseUrl}/api/stats/top-products`, { params: { ...params, limit: 10 } }),
        axios.get(`${baseUrl}/api/stats/low-stock`, { params: { admin_user_id: userId, threshold: 10 } })
      ]);

      setFinancialData(financial.data);
      setPerformanceData(performance.data);
      setTopSellers(sellers.data.sellers);
      setTopProducts(products.data.products);
      setLowStock(stock.data);
    } catch (err) {
      console.error('Error fetching statistics:', err);
      setError('Error al cargar las estadísticas');
    } finally {
      setLoading(false);
    }
  }

  async function downloadExcel() {
    setDownloadingExcel(true);
    try {
      const params = {
        admin_user_id: userId,
        period: period
      };

      if (period === 'custom' && customStartDate && customEndDate) {
        params.start_date = customStartDate;
        params.end_date = customEndDate;
      }

      const response = await axios.get(`${baseUrl}/api/stats/delivery-performance`, { params });
      const data = response.data;

      if (!data.deliveries || data.deliveries.length === 0) {
        alert('No hay datos de entregas para exportar en este periodo');
        return;
      }

      // Prepare data for Excel
      const excelData = data.deliveries.map((delivery, index) => ({
        'N°': index + 1,
        'ID Entrega': delivery.id,
        'Usuario': delivery.username,
        'Fecha': delivery.delivery_date,
        'Cliente': delivery.recipient_name,
        'Producto': delivery.product,
        'Estado': delivery.status,
        'Notas Conductor': delivery.notes_driver || '',
        'Monto Venta (S/)': delivery.charge ? delivery.charge.toFixed(2) : '0.00',
        'Cobrado (S/)': delivery.charged ? delivery.charged.toFixed(2) : '0.00',
        'FastMax (S/)': delivery.fastmax_transfer_amount ? delivery.fastmax_transfer_amount.toFixed(2) : '0.00',
        'Cambio (S/)': delivery.fastmax_exchange_amount ? delivery.fastmax_exchange_amount.toFixed(2) : '0.00',
        'Total Cobrado (S/)': delivery.total_charged ? delivery.total_charged.toFixed(2) : '0.00'
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(excelData);

      // Set column widths
      ws['!cols'] = [
        { wch: 5 },   // N°
        { wch: 12 },  // ID Entrega
        { wch: 20 },  // Usuario
        { wch: 12 },  // Fecha
        { wch: 25 },  // Cliente
        { wch: 30 },  // Producto
        { wch: 12 },  // Estado
        { wch: 30 },  // Notas Conductor
        { wch: 15 },  // Monto Venta
        { wch: 12 },  // Cobrado
        { wch: 12 },  // FastMax
        { wch: 12 },  // Cambio
        { wch: 15 }   // Total Cobrado
      ];

      // Create workbook
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Entregas');

      // Add summary sheet with enhanced data
      const summaryData = [
        { 'Métrica': 'RESUMEN GENERAL', 'Valor': '' },
        { 'Métrica': '', 'Valor': '' },
        { 'Métrica': 'Total Entregas', 'Valor': data.total_deliveries },
        { 'Métrica': 'Total Paquetes', 'Valor': data.total_packages },
        { 'Métrica': 'Tasa de Éxito (%)', 'Valor': data.success_rate },
        { 'Métrica': '', 'Valor': '' },
        { 'Métrica': 'DESGLOSE POR ESTADO', 'Valor': '' },
        { 'Métrica': '', 'Valor': '' }
      ];

      // Add status breakdown
      Object.entries(data.status_breakdown).forEach(([status, count]) => {
        summaryData.push({
          'Métrica': `  ${status}`,
          'Valor': `${count} (${data.status_percentages[status]}%)`
        });
      });

      // Add user statistics section
      if (data.user_stats && Object.keys(data.user_stats).length > 0) {
        summaryData.push({ 'Métrica': '', 'Valor': '' });
        summaryData.push({ 'Métrica': '', 'Valor': '' });
        summaryData.push({ 'Métrica': 'ENTREGAS POR USUARIO', 'Valor': '' });
        summaryData.push({ 'Métrica': '', 'Valor': '' });

        // Sort users by total deliveries (descending)
        const sortedUsers = Object.entries(data.user_stats).sort((a, b) => b[1].total - a[1].total);

        sortedUsers.forEach(([username, stats]) => {
          summaryData.push({
            'Métrica': `${username}`,
            'Valor': `Total: ${stats.total}`
          });
          summaryData.push({
            'Métrica': '  Entregados',
            'Valor': stats.entregado
          });
          summaryData.push({
            'Métrica': '  Rechazados',
            'Valor': stats.rechazado
          });
          summaryData.push({
            'Métrica': '  Cancelados',
            'Valor': stats.cancelado
          });
          if (stats.pending > 0) {
            summaryData.push({
              'Métrica': '  Pendientes',
              'Valor': stats.pending
            });
          }
          if (stats.pospuesto > 0) {
            summaryData.push({
              'Métrica': '  Pospuestos',
              'Valor': stats.pospuesto
            });
          }
          if (stats.encamino > 0) {
            summaryData.push({
              'Métrica': '  En Camino',
              'Valor': stats.encamino
            });
          }
          summaryData.push({ 'Métrica': '', 'Valor': '' });
        });
      }

      const wsSummary = XLSX.utils.json_to_sheet(summaryData);
      wsSummary['!cols'] = [{ wch: 35 }, { wch: 25 }];
      XLSX.utils.book_append_sheet(wb, wsSummary, 'Resumen');

      // Generate filename with date
      const periodLabel = period === 'custom' 
        ? `${customStartDate}_${customEndDate}`
        : period;
      const filename = `Rendimiento_Entregas_${periodLabel}_${new Date().toISOString().split('T')[0]}.xlsx`;

      // Download file
      XLSX.writeFile(wb, filename);

    } catch (err) {
      console.error('Error downloading Excel:', err);
      alert('Error al descargar el archivo Excel');
    } finally {
      setDownloadingExcel(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-12 h-12 text-brand-primary animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <p className="text-red-700 font-montserrat">{error}</p>
        <button
          onClick={fetchAllData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg font-montserrat hover:bg-red-700 transition-colors"
        >
          Reintentar
        </button>
      </div>
    );
  }

  const hasPaymentImages = financialData?.payment_images && financialData.payment_images.length > 0;

  return (
    <div className="space-y-6">
      {/* Financial Overview */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 font-montserrat mb-4 flex items-center gap-2">
          <DollarSign className="w-6 h-6 text-brand-primary" />
          Resumen Financiero
        </h2>
        
        {/* First Row - Main Metrics */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <StatCard
            icon={DollarSign}
            label="Total Cobrado"
            value={`S/ ${financialData?.total_charged?.toFixed(2) || '0.00'}`}
            subValue={`${financialData?.billable_deliveries || 0} entregas facturables`}
            gradient="from-brand-green to-brand-gold"
          />
          <StatCard
            icon={TrendingUp}
            label="Pendiente de Cobro"
            value={`S/ ${financialData?.total_to_collect?.toFixed(2) || '0.00'}`}
            gradient="from-brand-gold to-brand-primary"
          />
          <StatCard
            icon={Package}
            label="Comisión Fast Max"
            value={`S/ ${financialData?.service_revenue?.toFixed(2) || '0.00'}`}
            gradient="from-brand-primary to-brand-gold"
          />
          <StatCard
            icon={ShoppingBag}
            label="Valor Promedio"
            value={`S/ ${financialData?.average_order_value?.toFixed(2) || '0.00'}`}
            subValue="Por entrega exitosa"
            gradient="from-brand-green to-brand-primary"
          />
        </div>

        {/* Second Row - Net Deposit (Highlighted) */}
        <div className="bg-gradient-to-r from-brand-primary/10 via-brand-gold/10 to-brand-green/10 rounded-xl p-6 border-2 border-brand-primary">
          <div className="flex items-center gap-4">
            <div className="relative"> {/* Add relative wrapper here */}
              <button
                onClick={() => hasPaymentImages && setShowImageModal(true)}
                disabled={!hasPaymentImages}
                className={`w-16 h-16 rounded-xl bg-gradient-to-br from-brand-primary to-brand-gold flex items-center justify-center shadow-lg flex-shrink-0 transition-all ${
                  hasPaymentImages 
                    ? 'hover:scale-110 hover:shadow-xl cursor-pointer' 
                    : 'opacity-60 cursor-not-allowed'
                }`}
                title={hasPaymentImages ? 'Ver comprobantes de pago' : 'No hay comprobantes disponibles'}
              >
                <Wallet className="w-8 h-8 text-white" />
              </button>
              {hasPaymentImages && (
                <div className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center shadow-lg border-2 border-white">
                  <span className="text-xs text-white font-bold font-montserrat">
                    {financialData.payment_images.length}
                  </span>
                </div>
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm text-slate-600 font-montserrat mb-1 flex items-center gap-2">
                <ArrowDownCircle className="w-4 h-4 text-brand-primary" />
                Monto Neto a Depositar
                {hasPaymentImages && (
                  <span className="text-xs bg-brand-gold/20 text-brand-gold px-2 py-1 rounded-full font-semibold flex items-center gap-1">
                    <ImageIcon className="w-3 h-3" />
                    {financialData.payment_images.length} comprobante{financialData.payment_images.length !== 1 ? 's' : ''}
                  </span>
                )}
              </p>
              <p className="text-4xl font-bold text-brand-primary font-montserrat">
                S/ {financialData?.net_to_deposit?.toFixed(2) || '0.00'}
              </p>
              <p className="text-xs text-slate-600 font-montserrat mt-1">
                Total cobrado (S/ {financialData?.total_charged?.toFixed(2) || '0.00'}) 
                {' - '}
                Servicio Fast Max (S/ {financialData?.service_revenue?.toFixed(2) || '0.00'})
                {financialData?.total_exchange > 0 && (
                  <>
                    {' - '}
                    Cambio (S/ {financialData?.total_exchange?.toFixed(2) || '0.00'})
                  </>
                )}
              </p>
            </div>
            <div className="hidden sm:block text-right">
              <div className="bg-white rounded-lg px-4 py-2 border border-brand-primary/20">
                <p className="text-xs text-slate-600 font-montserrat mb-1">
                  Entregas Facturables
                </p>
                <p className="text-2xl font-bold text-slate-900 font-montserrat">
                  {financialData?.billable_deliveries || 0}
                </p>
                <p className="text-xs text-slate-500 font-montserrat">
                  Tarifa variable
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delivery Performance */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-slate-900 font-montserrat flex items-center gap-2">
            <Package className="w-6 h-6 text-brand-primary" />
            Rendimiento de Entregas
          </h2>
          <button
            onClick={downloadExcel}
            disabled={downloadingExcel}
            className="flex items-center gap-2 px-4 py-2 bg-brand-green text-white rounded-lg font-montserrat hover:bg-brand-green/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
          >
            {downloadingExcel ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Descargando...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Descargar Excel
              </>
            )}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
          <StatCard
            icon={CheckCircle}
            label="Entregados"
            value={performanceData?.status_breakdown?.entregado || 0}
            subValue={`${performanceData?.status_percentages?.entregado || 0}%`}
            gradient="from-brand-green to-brand-gold"
          />
          <StatCard
            icon={XCircle}
            label="Rechazados"
            value={performanceData?.status_breakdown?.rechazado || 0}
            subValue={`${performanceData?.status_percentages?.rechazado || 0}%`}
            gradient="from-red-500 to-red-600"
          />
          <StatCard
            icon={Ban}
            label="Cancelados"
            value={performanceData?.status_breakdown?.cancelado || 0}
            subValue={`${performanceData?.status_percentages?.cancelado || 0}%`}
            gradient="from-slate-500 to-slate-600"
          />
        </div>

        {/* Status Breakdown Chart */}
        <StatusBreakdownChart data={performanceData} />
      </div>

      {/* Top Sellers and Products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h2 className="text-xl font-bold text-slate-900 font-montserrat mb-4 flex items-center gap-2">
            <Store className="w-6 h-6 text-brand-primary" />
            Top Vendedores
          </h2>
          <TopSellersTable sellers={topSellers} />
        </div>

        <div>
          <h2 className="text-xl font-bold text-slate-900 font-montserrat mb-4 flex items-center gap-2">
            <ShoppingBag className="w-6 h-6 text-brand-primary" />
            Productos Más Vendidos
          </h2>
          <TopProductsTable products={topProducts} />
        </div>
      </div>

      {/* Low Stock Alerts */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 font-montserrat mb-4 flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-brand-primary" />
          Alertas de Stock
        </h2>
        <LowStockAlerts data={lowStock} />
      </div>

      {/* Image Carousel Modal */}
      <ImageCarouselModal
        images={financialData?.payment_images || []}
        isOpen={showImageModal}
        onClose={() => setShowImageModal(false)}
      />
    </div>
  );
}

export default Phase1Dashboard;